import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@components/input';
import { LabelPrimary } from '@components/label';
import { SIZE_S } from '@components/pill-toggle';
import { PILL_TOGGLE_GROUP_VARIANT_TABS, PillToggleGroup } from '@components/pill-toggle-group';
import { Textarea } from '@components/textarea';
import { TextareaCounter } from '@components/textarea-counter';
import { SupportedLanguages } from '@constants/constants.type';
import classNames from 'classnames';

import { TranslationFieldsProps } from './translation-fields.type';
import { TranslationFieldsLoading } from './translation-fields-loading';
import { buildTabOptions } from './translation-fields-utils';

const DEFAULT_TITLE_MAX = 50;
const DEFAULT_DESCRIPTION_MAX = 200;

export const TranslationFields = ({
  value,
  onChange,
  mainLanguage,
  loading = false,
  disabled = false,
  titleMaxLength = DEFAULT_TITLE_MAX,
  descriptionMaxLength = DEFAULT_DESCRIPTION_MAX,
  titleLabel,
  descriptionLabel,
  titleAsTextarea = false,
  className,
}: TranslationFieldsProps) => {
  const { t, i18n } = useTranslation();
  const resolvedMainLanguage = (mainLanguage ?? i18n.language) as SupportedLanguages;

  const options = useMemo(() => buildTabOptions(resolvedMainLanguage), [resolvedMainLanguage]);
  const fallbackLocale = options[0]?.value ?? 'fr';
  // Start on the first tab (in priority order) that already has content, so existing translations
  // are immediately visible; a content-less map falls back to the first tab.
  const [activeLocale, setActiveLocale] = useState<SupportedLanguages>(() => {
    const firstWithContent = options.find((option) => {
      const content = value[option.value];
      return !!content?.title || !!content?.description;
    });
    return firstWithContent?.value ?? fallbackLocale;
  });
  const effectiveLocale = options.some((option) => option.value === activeLocale) ? activeLocale : fallbackLocale;

  const current = value[effectiveLocale] ?? { title: '', description: '' };

  const handleFieldChange = (field: 'title' | 'description', next: string) => {
    onChange({
      ...value,
      [effectiveLocale]: { ...current, [field]: next },
    });
  };

  if (loading) {
    return (
      <div className={classNames('flex flex-col gap-4', className)}>
        <TranslationFieldsLoading />
      </div>
    );
  }

  return (
    <div className={classNames('flex flex-col gap-4', className)}>
      <PillToggleGroup<SupportedLanguages>
        value={effectiveLocale}
        onChange={setActiveLocale}
        options={options}
        size={SIZE_S}
        variant={PILL_TOGGLE_GROUP_VARIANT_TABS}
        fullWidth
        ariaLabel='Translation language'
        dataTestId='translation-fields-language'
      />
      <div className='flex flex-col' data-testid='translation-fields-title'>
        <LabelPrimary>{titleLabel}</LabelPrimary>
        {titleAsTextarea ? (
          <Textarea
            value={current.title}
            maxLength={titleMaxLength}
            disabled={disabled}
            className='h-24 md:h-[74px] resize-none'
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('title', event.target.value)}
          />
        ) : (
          <Input
            value={current.title}
            maxLength={titleMaxLength}
            disabled={disabled}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleFieldChange('title', event.target.value)}
          />
        )}
      </div>
      <div className='flex flex-col'>
        <LabelPrimary>{descriptionLabel}</LabelPrimary>
        <TextareaCounter
          value={current.description}
          length={current.description.length}
          maxLength={descriptionMaxLength}
          charactersText={t('common:characters')}
          rows={5}
          disabled={disabled}
          className='scrollbar-xceed'
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => handleFieldChange('description', event.target.value)}
        />
      </div>
    </div>
  );
};
